import React, { useEffect, useMemo } from 'react'
import {
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesInitialized,
  useNodesState,
  useReactFlow,
  type Edge,
  type Node,
} from '@xyflow/react'

import { PROGRESS_NODE_TYPES, type WorkflowProgressState } from './ProgressNodes'
import {
  EDGE_PATH_STYLE,
  PROGRESS_LAYOUT,
  publishPosition,
  startPosition,
  stepPosition,
} from './layout'

const START_NODE_ID = '__start__'
const PUBLISH_NODE_ID = '__publish__'

const FIT_VIEW_OPTIONS = { padding: 0.14, maxZoom: 1 }
const PRO_OPTIONS = { hideAttribution: false }

export interface WorkflowProgressStep {
  index: number
  name: string
  state: WorkflowProgressState
  overdue?: boolean
  /** Already formatted by the caller — the detail page owns the app's date
   *  formatting and this module deliberately holds no locale logic. */
  dueLabel?: string | null
}

export interface WorkflowProgressFlowProps {
  steps: WorkflowProgressStep[]
  targetFolderName?: string | null
}

const WorkflowProgressFlowCanvas: React.FC<WorkflowProgressFlowProps> = ({ steps, targetFolderName }) => {
  const { fitView } = useReactFlow()
  const nodesInitialized = useNodesInitialized()
  const [nodes, setNodes] = useNodesState<Node>([])
  const [edges, setEdges] = useEdgesState<Edge>([])

  const signature = useMemo(
    () =>
      `${targetFolderName ?? ''}#` +
      steps.map((s) => `${s.index}~${s.name}~${s.state}~${s.overdue ? 1 : 0}~${s.dueLabel ?? ''}`).join('|'),
    [steps, targetFolderName],
  )

  useEffect(() => {
    const nextNodes: Node[] = [
      { id: START_NODE_ID, type: 'start', position: startPosition(PROGRESS_LAYOUT), data: {}, draggable: false },
    ]

    steps.forEach((step, position) => {
      nextNodes.push({
        id: `step-${step.index}`,
        type: 'step',
        position: stepPosition(PROGRESS_LAYOUT, position),
        draggable: false,
        data: {
          index: step.index,
          name: step.name,
          state: step.state,
          overdue: !!step.overdue,
          dueLabel: step.dueLabel ?? null,
        },
      })
    })

    nextNodes.push({
      id: PUBLISH_NODE_ID,
      type: 'publish',
      position: publishPosition(PROGRESS_LAYOUT, steps.length),
      draggable: false,
      data: { folderName: targetFolderName ?? null },
    })

    const chain = [START_NODE_ID, ...steps.map((s) => `step-${s.index}`), PUBLISH_NODE_ID]
    const nextEdges: Edge[] = []
    for (let i = 0; i < chain.length - 1; i += 1) {
      nextEdges.push({
        id: `${chain[i]}->${chain[i + 1]}`,
        source: chain[i],
        target: chain[i + 1],
        type: 'straight',
        style: EDGE_PATH_STYLE,
        selectable: false,
        focusable: false,
        deletable: false,
      })
    }

    setNodes(nextNodes)
    setEdges(nextEdges)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature])

  // Same measurement-aware refit as the editor — see the comment there.
  useEffect(() => {
    if (nodesInitialized) fitView(FIT_VIEW_OPTIONS)
  }, [nodesInitialized, steps.length, fitView])

  return (
    <div className="h-[170px] overflow-hidden rounded-md border bg-muted/20">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={PROGRESS_NODE_TYPES}
        nodesDraggable={false}
        nodesConnectable={false}
        nodesFocusable={false}
        edgesFocusable={false}
        elementsSelectable={false}
        panOnDrag={false}
        panOnScroll={false}
        zoomOnScroll={false}
        zoomOnPinch={false}
        zoomOnDoubleClick={false}
        preventScrolling={false}
        deleteKeyCode={null}
        minZoom={0.3}
        maxZoom={1}
        fitView
        fitViewOptions={FIT_VIEW_OPTIONS}
        proOptions={PRO_OPTIONS}
      />
    </div>
  )
}

/**
 * Read-only mirror of the workflow plan on the submittal detail: the same
 * `[Start] → steps → [Publish]` chain the editor draws, coloured by how far the
 * review has actually got. Every interaction is off — it is a picture, not a
 * control; the grouped review list below it stays the place where verdicts are
 * given.
 */
const WorkflowProgressFlow: React.FC<WorkflowProgressFlowProps> = (props) => (
  <ReactFlowProvider>
    <WorkflowProgressFlowCanvas {...props} />
  </ReactFlowProvider>
)

export default WorkflowProgressFlow
